/* eslint-disable react-refresh/only-export-components */
// ════════════════════════════════════════════════════════════════════
// Workflow editor — node renderers (custom React Flow node components).
// Single file for compactness: 11 node types, each rendered as a small
// card with input/output handles. Used by both the read-only canvas
// preview and the full editor.
// ════════════════════════════════════════════════════════════════════
import { Handle, Position, type NodeProps, type Node } from "@xyflow/react";
import { Badge } from "@/components/ui/badge";
import {
  Zap, CheckCircle2, ListChecks, Bot, Bell, GitBranch, GitFork,
  Merge, Timer, Shuffle, CircleStop, AlertCircle,
} from "lucide-react";
import type { WorkflowStep, WorkflowStepType } from "@/lib/api";

export const NODE_WIDTH = 240;
export const NODE_HEIGHT = 96;

export type StepNodeData = {
  step: WorkflowStep;
  isTrigger?: boolean;
  triggerLabel?: string;
  selected?: boolean;
  invalid?: boolean;
};

export type FlowNode = Node<StepNodeData>;

// ── Step-type → icon + tint ─────────────────────────────────────────
export const STEP_META: Record<WorkflowStepType, { icon: typeof Zap; tint: string; label: string }> = {
  approval:       { icon: CheckCircle2, tint: "emerald",  label: "Approval" },
  task:           { icon: ListChecks,   tint: "blue",     label: "Task" },
  service_action: { icon: Bot,          tint: "violet",   label: "Service Action" },
  notification:   { icon: Bell,         tint: "amber",    label: "Notification" },
  condition:      { icon: GitBranch,    tint: "rose",     label: "Condition" },
  parallel:       { icon: GitFork,      tint: "cyan",     label: "Parallel" },
  merge:          { icon: Merge,        tint: "indigo",   label: "Merge" },
  delay:          { icon: Timer,        tint: "yellow",   label: "Delay" },
  switch:         { icon: Shuffle,      tint: "fuchsia",  label: "Switch" },
  end:            { icon: CircleStop,   tint: "slate",    label: "End" },
};

// Tailwind tint utility — used by both nodes & palette so they match.
export function tintClasses(t: string) {
  // Pre-listed so Tailwind JIT picks them up at build time.
  switch (t) {
    case "emerald": return { text: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/40", dot: "!bg-emerald-500" };
    case "blue":    return { text: "text-blue-600 dark:text-blue-400",       bg: "bg-blue-500/10",    border: "border-blue-500/40",    dot: "!bg-blue-500" };
    case "violet":  return { text: "text-violet-600 dark:text-violet-400",   bg: "bg-violet-500/10",  border: "border-violet-500/40",  dot: "!bg-violet-500" };
    case "amber":   return { text: "text-amber-600 dark:text-amber-400",     bg: "bg-amber-500/10",   border: "border-amber-500/40",   dot: "!bg-amber-500" };
    case "rose":    return { text: "text-rose-600 dark:text-rose-400",       bg: "bg-rose-500/10",    border: "border-rose-500/40",    dot: "!bg-rose-500" };
    case "cyan":    return { text: "text-cyan-600 dark:text-cyan-400",       bg: "bg-cyan-500/10",    border: "border-cyan-500/40",    dot: "!bg-cyan-500" };
    case "indigo":  return { text: "text-indigo-600 dark:text-indigo-400",   bg: "bg-indigo-500/10",  border: "border-indigo-500/40",  dot: "!bg-indigo-500" };
    case "yellow":  return { text: "text-yellow-600 dark:text-yellow-400",   bg: "bg-yellow-500/10",  border: "border-yellow-500/40",  dot: "!bg-yellow-500" };
    case "fuchsia": return { text: "text-fuchsia-600 dark:text-fuchsia-400", bg: "bg-fuchsia-500/10", border: "border-fuchsia-500/40", dot: "!bg-fuchsia-500" };
    default:        return { text: "text-slate-600 dark:text-slate-400",     bg: "bg-slate-500/10",   border: "border-slate-500/40",   dot: "!bg-slate-500" };
  }
}

// ── Shared frame ────────────────────────────────────────────────────
function NodeFrame({
  selected, invalid, tint, icon: Icon, kindLabel, title, subtitle, children,
}: {
  selected?: boolean; invalid?: boolean; tint: string;
  icon: typeof Zap; kindLabel: string;
  title: string; subtitle?: React.ReactNode; children?: React.ReactNode;
}) {
  const c = tintClasses(tint);
  const ringClass = invalid
    ? "border-rose-500 ring-2 ring-rose-500/30"
    : selected
      ? `${c.border} ring-2 ring-offset-1 ring-offset-background ring-primary/40`
      : "border-border hover:border-primary/60";
  return (
    <div className={`w-[${NODE_WIDTH}px] rounded-lg border-2 bg-card shadow-sm overflow-hidden transition-colors ${ringClass}`}
         style={{ width: NODE_WIDTH }}>
      <div className={`flex items-center gap-2 px-3 py-2 border-b ${c.bg} ${c.border}`}>
        <Icon className={`w-4 h-4 shrink-0 ${c.text}`} />
        <span className={`text-[10px] font-semibold uppercase tracking-wider ${c.text}`}>
          {kindLabel}
        </span>
        {invalid && <AlertCircle className="w-3 h-3 text-rose-500 ml-auto" />}
      </div>
      <div className="px-3 py-2.5">
        <p className="text-sm font-medium text-foreground truncate">{title}</p>
        {subtitle && <div className="mt-1">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

// ── Trigger node (synthetic, always first) ──────────────────────────
export function TriggerNode({ data }: NodeProps<FlowNode>) {
  return (
    <>
      <NodeFrame
        tint="violet" icon={Zap} kindLabel="Trigger"
        title={data.triggerLabel || "Workflow trigger"}
      />
      <Handle type="source" position={Position.Bottom} className="!w-2.5 !h-2.5 !bg-violet-500" />
    </>
  );
}

// ── Standard 1-in/1-out node ────────────────────────────────────────
export function StepNode({ data, selected }: NodeProps<FlowNode>) {
  const meta = STEP_META[data.step.step_type] || STEP_META.task;
  return (
    <>
      <Handle type="target" position={Position.Top} className="!w-2.5 !h-2.5 !bg-muted-foreground" />
      <NodeFrame
        tint={meta.tint} icon={meta.icon} kindLabel={meta.label}
        title={data.step.name || "(unnamed)"}
        invalid={data.invalid} selected={selected}
        subtitle={
          <div className="flex flex-wrap gap-1">
            {data.step.assign_to_role && (
              <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-4">{data.step.assign_to_role}</Badge>
            )}
            {data.step.step_type === "service_action" && data.step.config?.action && (
              <Badge variant="outline" className="text-[10px] py-0 px-1.5 h-4 font-mono">
                {String(data.step.config.action)}
              </Badge>
            )}
          </div>
        }
      />
      <Handle type="source" position={Position.Bottom} className="!w-2.5 !h-2.5 !bg-muted-foreground" />
    </>
  );
}

// ── Condition node — 2 outputs (true / false) ───────────────────────
export function ConditionNode({ data, selected }: NodeProps<FlowNode>) {
  const meta = STEP_META.condition;
  return (
    <>
      <Handle type="target" position={Position.Top} className="!w-2.5 !h-2.5 !bg-muted-foreground" />
      <NodeFrame
        tint={meta.tint} icon={meta.icon} kindLabel={meta.label}
        title={data.step.name || "(unnamed)"}
        invalid={data.invalid} selected={selected}
        subtitle={data.step.config?.field ? (
          <p className="text-[11px] text-muted-foreground truncate">
            <code className="font-mono">{String(data.step.config.field)}</code>
            {" "}{String(data.step.config.operator || "eq")}{" "}
            <code className="font-mono">"{String(data.step.config.equals ?? data.step.config.value ?? "")}"</code>
          </p>
        ) : undefined}
      >
        <div className="flex justify-between px-3 pb-2 text-[10px] font-medium">
          <span className="text-emerald-600">true</span>
          <span className="text-rose-600">false</span>
        </div>
      </NodeFrame>
      <Handle type="source" id="true"  position={Position.Bottom} style={{ left: "25%" }}
              className="!w-2.5 !h-2.5 !bg-emerald-500" />
      <Handle type="source" id="false" position={Position.Bottom} style={{ left: "75%" }}
              className="!w-2.5 !h-2.5 !bg-rose-500" />
    </>
  );
}

// ── Parallel — fans out to N branches ───────────────────────────────
export function ParallelNode({ data, selected }: NodeProps<FlowNode>) {
  const meta = STEP_META.parallel;
  const n = Math.max(2, Math.min(8, data.step.outputs_count || 2));
  return (
    <>
      <Handle type="target" position={Position.Top} className="!w-2.5 !h-2.5 !bg-muted-foreground" />
      <NodeFrame
        tint={meta.tint} icon={meta.icon} kindLabel={meta.label}
        title={data.step.name || "(unnamed)"}
        invalid={data.invalid} selected={selected}
        subtitle={<p className="text-[11px] text-muted-foreground">{n} parallel branches</p>}
      >
        <div className="flex justify-around px-3 pb-2 text-[10px] font-medium text-cyan-600">
          {Array.from({ length: n }).map((_, i) => (
            <span key={i}>#{i + 1}</span>
          ))}
        </div>
      </NodeFrame>
      {Array.from({ length: n }).map((_, i) => (
        <Handle
          key={i}
          type="source" id={`branch_${i}`}
          position={Position.Bottom}
          style={{ left: `${((i + 0.5) / n) * 100}%` }}
          className="!w-2.5 !h-2.5 !bg-cyan-500"
        />
      ))}
    </>
  );
}

// ── Merge — waits for N inbound completions ─────────────────────────
export function MergeNode({ data, selected }: NodeProps<FlowNode>) {
  const meta = STEP_META.merge;
  return (
    <>
      <Handle type="target" position={Position.Top} className="!w-2.5 !h-2.5 !bg-indigo-500" />
      <NodeFrame
        tint={meta.tint} icon={meta.icon} kindLabel={meta.label}
        title={data.step.name || "(unnamed)"}
        invalid={data.invalid} selected={selected}
        subtitle={
          <p className="text-[11px] text-muted-foreground">
            Waits for {data.step.config?.wait_for_count || "all"} inbound
          </p>
        }
      />
      <Handle type="source" position={Position.Bottom} className="!w-2.5 !h-2.5 !bg-indigo-500" />
    </>
  );
}

// ── Delay — pause for N units ───────────────────────────────────────
export function DelayNode({ data, selected }: NodeProps<FlowNode>) {
  const meta = STEP_META.delay;
  return (
    <>
      <Handle type="target" position={Position.Top} className="!w-2.5 !h-2.5 !bg-muted-foreground" />
      <NodeFrame
        tint={meta.tint} icon={meta.icon} kindLabel={meta.label}
        title={data.step.name || "(unnamed)"}
        invalid={data.invalid} selected={selected}
        subtitle={
          <p className="text-[11px] text-muted-foreground">
            {data.step.config?.delay_days ?? 0} day(s)
          </p>
        }
      />
      <Handle type="source" position={Position.Bottom} className="!w-2.5 !h-2.5 !bg-yellow-500" />
    </>
  );
}

// ── Switch — n cases, one default ───────────────────────────────────
export function SwitchNode({ data, selected }: NodeProps<FlowNode>) {
  const meta = STEP_META.switch;
  const cases: any[] = Array.isArray(data.step.config?.cases) ? data.step.config!.cases : [];
  const n = Math.max(1, Math.min(8, cases.length || data.step.outputs_count || 2));
  return (
    <>
      <Handle type="target" position={Position.Top} className="!w-2.5 !h-2.5 !bg-muted-foreground" />
      <NodeFrame
        tint={meta.tint} icon={meta.icon} kindLabel={meta.label}
        title={data.step.name || "(unnamed)"}
        invalid={data.invalid} selected={selected}
        subtitle={
          <p className="text-[11px] text-muted-foreground truncate">
            {data.step.config?.field ? <><code className="font-mono">{String(data.step.config.field)}</code> — </> : null}
            {n} case(s)
          </p>
        }
      >
        <div className="flex justify-around px-3 pb-2 text-[10px] font-medium text-fuchsia-600">
          {Array.from({ length: n }).map((_, i) => (
            <span key={i}>{cases[i]?.value || `case ${i + 1}`}</span>
          ))}
        </div>
      </NodeFrame>
      {Array.from({ length: n }).map((_, i) => (
        <Handle
          key={i}
          type="source" id={`case_${i}`}
          position={Position.Bottom}
          style={{ left: `${((i + 0.5) / n) * 100}%` }}
          className="!w-2.5 !h-2.5 !bg-fuchsia-500"
        />
      ))}
    </>
  );
}

// ── End — explicit terminator ───────────────────────────────────────
export function EndNode({ data, selected }: NodeProps<FlowNode>) {
  const meta = STEP_META.end;
  return (
    <>
      <Handle type="target" position={Position.Top} className="!w-2.5 !h-2.5 !bg-slate-500" />
      <NodeFrame
        tint={meta.tint} icon={meta.icon} kindLabel={meta.label}
        title={data.step.name || "End"}
        invalid={data.invalid} selected={selected}
      />
    </>
  );
}

// ── Registry — passed to <ReactFlow nodeTypes={...} /> ──────────────
export const NODE_TYPES = {
  trigger:        TriggerNode,
  step:           StepNode,        // approval / task / service_action / notification
  condition:      ConditionNode,
  parallel:       ParallelNode,
  merge:          MergeNode,
  delay:          DelayNode,
  switch:         SwitchNode,
  end:            EndNode,
};

// Map a workflow step's step_type to the React Flow node type registered above.
export function flowNodeTypeFor(stepType: WorkflowStepType): keyof typeof NODE_TYPES {
  switch (stepType) {
    case "condition": return "condition";
    case "parallel":  return "parallel";
    case "merge":     return "merge";
    case "delay":     return "delay";
    case "switch":    return "switch";
    case "end":       return "end";
    default:          return "step";
  }
}
